import { AUTH_STATE } from '@marriott/mi-headless-utils';
import { STATUS_IN_BOOLEAN } from '../constants/constant';
import { accountAgeCalculator, sanitizeObject, ternaryHandler, updateDateFormat } from '../helpers';
type STATUS_TYPE = keyof typeof STATUS_IN_BOOLEAN;

/**
 *
 * Generates ACDL (Adobe Client Data Layer) keys for PAGEVIEW events.
 * @param {Object} args
 * @param {dataLayerType} args.datalayer - The current dataLayer avaliable
 * @param {boolean} args.isModel - Flag indicating if it's model based PAGEVIEW
 * @param {string} args.modelName - name of the model if it's model based tracking
 * @returns {Object} - Returns an object containing metadata, page & user data
 */
export const pageViewACDLkeyGenerator = ({ datalayer, isModel, modelName, sectionName= 'Luxury' }: pageViewACDLkeyGeneratorType): object => {
  const payload = {
    metadata: {
      allow_marketing: true, //always true if ACDL enabled
      site_id: ternaryHandler(datalayer?.['env_site_id']),
      language_domain: ternaryHandler(datalayer?.['env_site_id']),
      platform: ternaryHandler(datalayer?.['env_platform']),
      server_id: ternaryHandler(datalayer?.['env_server_id']),
      session_id: ternaryHandler(datalayer?.['sessionId']),
      latitude: ternaryHandler(datalayer?.['browser_akamai_loc_lat']),
      longitude: ternaryHandler(datalayer?.['browser_akamai_loc_long']),
      geo_country: ternaryHandler(datalayer?.['browser_akamai_loc_country']),
      geo_state: ternaryHandler(datalayer?.['browser_akamai_loc_state']),
      geo_city: ternaryHandler(datalayer?.['browser_akamai_loc_city']),
    },
    page: {
      name: isModel ? modelName : window.location.host + window.location.pathname + window.location.hash, // model name need to confirm
      section: sectionName,
    },
    // key value pairs of data about the user
    user: {
      alt_customer_id: ternaryHandler(datalayer?.['altCustId'], ternaryHandler(datalayer?.['mr_id_alternate'])),
      authentication_status: ternaryHandler(datalayer?.['memState']),
      member_level: ternaryHandler(datalayer?.['mr_prof_member_program_level']),
      member_city: ternaryHandler(datalayer?.['mr_prof_address_city']),
      member_state: ternaryHandler(datalayer?.['mr_prof_address_state']),
      member_country: ternaryHandler(datalayer?.['mr_prof_address_country']),
      upcoming_stay: ternaryHandler(datalayer?.['mr_prof_upcoming_stay']),
      stay_in_progress: ternaryHandler(datalayer?.['mr_prof_stay_in_progress']),
      cc_all: ternaryHandler(datalayer?.['mr_prof_cc_all']),
      nights_booked_this_year: ternaryHandler(datalayer?.['mr_prof_nights_booked_this_year']),
      nights_to_renew_current_member_level: ternaryHandler(datalayer?.['mr_prof_nights_to_renew_current']),
      points_balance: ternaryHandler(datalayer?.['mr_prof_points_balance']),
      member_join_date: updateDateFormat(ternaryHandler(datalayer?.['mr_prof_join_date']) as string),
      account_age: accountAgeCalculator(datalayer ?? {}),
    },
  };
  const cleanedPayload = sanitizeObject(payload);
  return cleanedPayload;
};

/**
 *
 * @param jsonObj refers to pageModelJSON
 * @param compType AEM mapping url of the component
 * @returns updated model json
 * @description it will iterate over the model json and will look for that compType,
 * if it exists then it will inject property to that component model json
 *
 */
export const updateModelJSON = (jsonObj: Record<string, unknown>, compType: string[] = []) => {
  function traverse(obj: Record<string, unknown>) {
    if (obj !== null && typeof obj === 'object') {
      if (compType.includes(String(obj[':type']))) {
        obj['isHide'] = true;
        obj['mboxParameter'] = undefined;
        return true;
      }

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          traverse(obj[key] as Record<string, unknown>);
        }
      }
    }
    return false;
  }

  traverse(jsonObj);
  return jsonObj;
};

/**
 *
 * @param model refers to the page model json
 * @returns boolean
 *
 * @description this method is useful to identify whether ACDL is enabled or not
 * based on locale.
 *
 * NOTE: PLEASE RE-USE THIS METHOD ON SERVER SIDE ONLY, OTHERWISE ENV VALUES CANT BE FETCHED PROPERLY..
 */
export const isACDLEnabled = (model: Record<string, unknown>) => {
  const ACDL_ENABLED_LOCALES = String(process.env['NEXT_PUBLIC_ACDL_ENABLE_LOCALES'])?.split(',');
  return (
    String(process.env['NEXT_PUBLIC_ENABLE_WEBSDK']) === 'true' &&
    String(model?.['enableWebSDK']) === 'true' &&
    ACDL_ENABLED_LOCALES?.includes(String(model?.['language'])?.replace('_', '-')?.toLocaleLowerCase())
  );
};
